import { useState } from 'react'
import { updateSettings } from '@/data/user'
import { enablePush, mockPush, pushAvailable, usePush } from '@/lib/push'
import { isIos, isStandalone, needsHomeScreenForPush } from '@/lib/pwa'
import { useAuth } from '@/store/auth'
import { useHousehold } from '@/store/household'
import { useUi } from '@/store/ui'
import { Button } from './Button'
import { Row, RowCard, Switch, TimeRow } from './Rows'

/**
 * Notifications in Settings: the one button that asks permission, then the
 * three toggles and the summary time. On iPhone the install steps come first,
 * because web push there works only from the Home Screen.
 */
export function NotificationSettings() {
  const user = useAuth((s) => s.user)!
  const settings = useHousehold((s) => s.userDoc?.settings)
  const online = useUi((s) => s.online)
  const enabled = usePush((s) => s.enabled)
  const busy = usePush((s) => s.busy)
  const error = usePush((s) => s.error)
  const permission = usePush((s) => s.permission)
  const [available] = useState(() => pushAvailable() || mockPush)

  const n = settings?.notifications ?? { reminders: true, summary: true, partner: true }
  const toggle = (key: keyof typeof n) => void updateSettings(user.uid, { notifications: { ...n, [key]: !n[key] } })

  const blockedOnIos = needsHomeScreenForPush()
  const denied = permission === 'denied' && !mockPush

  return (
    <div className="settings__stack">
      {blockedOnIos ? (
        <p className="settings__line">
          On iPhone, notifications work only from the installed app. In Safari, tap <strong>Share</strong>, then <strong>Add to Home Screen</strong>, open {isIos() && !isStandalone() ? 'it from there' : 'the app'}, and come back here.
        </p>
      ) : enabled ? (
        <p className="settings__line">Notifications are on for this device.</p>
      ) : (
        <>
          <Button variant="primary" onClick={() => void enablePush(user.uid).catch(() => undefined)} disabled={busy || !online || !available || denied}>
            Turn on notifications on this device
          </Button>
          {denied && <p className="settings__line settings__line--muted">This browser has blocked notifications for the app; allow them in its site settings first.</p>}
          {!available && <p className="settings__line settings__line--muted">This browser cannot show notifications.</p>}
          {!online && <p className="settings__line settings__line--muted">Needs a connection.</p>}
          {error && <p className="onboard__error">{error}</p>}
        </>
      )}

      <RowCard>
        <Row label="Task reminders" hint="A timed task reaches its reminder time." onClick={() => toggle('reminders')} ariaPressed={n.reminders} tall>
          <Switch on={n.reminders} />
        </Row>
        <Row label="Morning summary" hint="Due today, overdue, and Waiting tasks to follow up." onClick={() => toggle('summary')} ariaPressed={n.summary} tall>
          <Switch on={n.summary} />
        </Row>
        {n.summary && <TimeRow id="summary-time" label="Summary time" value={settings?.summaryTime ?? '08:00'} onChange={(summaryTime) => void updateSettings(user.uid, { summaryTime })} />}
        <Row label="Partner activity" hint="The other person adds a task for either of you, or assigns one to you." onClick={() => toggle('partner')} ariaPressed={n.partner} tall>
          <Switch on={n.partner} />
        </Row>
      </RowCard>
    </div>
  )
}
